import {getComponentDisplayName} from '@modifi/utilities'
import React, {PureComponent} from 'react'

const withProgress = (options: {defaultExpectedDuration?: number; autoStart?: boolean}) => {
  const {defaultExpectedDuration = 2e3, autoStart = false} = options

  return <P extends Record<string, any>>(
    WrappedComponent: React.ComponentType<P>
  ): React.ComponentClass<Omit<P, 'progress' | 'progressStart' | 'progressDone'>> => {
    class ScopedComponent extends PureComponent<P, {progress: number}> {
      static displayName = `withProgress(${getComponentDisplayName(WrappedComponent)})`

      private timeout: number | undefined = undefined

      private progressInterval: number | undefined = undefined

      private done = false

      public constructor(props: any) {
        super(props)

        this.state = {
          progress: 0,
        }

        this.startTimer = this.startTimer.bind(this)
        this.finishProgress = this.finishProgress.bind(this)
        this.iterateProgress = this.iterateProgress.bind(this)
      }

      public componentDidMount() {
        if (autoStart) {
          this.startTimer()
        }
      }

      public componentWillUnmount() {
        clearTimeout(this.progressInterval)
        clearTimeout(this.timeout)
      }

      public startTimer(expectedDuration = defaultExpectedDuration) {
        this.done = false
        this.setState({progress: 0}, () => {
          requestAnimationFrame(() => {
            clearTimeout(this.timeout)
            clearTimeout(this.progressInterval)

            // setting a timeout for the overall completion of progress
            this.timeout = setTimeout(() => {
              this.finishProgress()
            }, expectedDuration) as unknown as number

            this.iterateProgress()
          })
        })
      }

      public iterateProgress() {
        if (this.done) {
          return
        }
        // next iteration is a value between 50 and 200
        const nextIteration = Math.round(Math.random() * 150 + 50)

        this.progressInterval = setTimeout(this.iterateProgress, nextIteration) as unknown as number
        const {progress: currentProgress} = this.state
        const progressLeft = 97 - currentProgress
        const progressAddition = (progressLeft * Math.random()) / 2
        const newProgress = Math.max(
          currentProgress,
          Math.round(currentProgress + Math.min(15, progressAddition))
        )

        this.setState(({progress}) => {
          const protectedProgress = progress > newProgress ? progress : newProgress
          return {progress: protectedProgress}
        })
      }

      public finishProgress() {
        this.done = true

        clearTimeout(this.progressInterval)
        clearTimeout(this.timeout)
        this.setState({progress: 100})
      }

      public render() {
        const {progress} = this.state
        return (
          <WrappedComponent
            progress={progress}
            progressStart={this.startTimer}
            progressDone={this.finishProgress}
            {...this.props}
          />
        )
      }
    }

    return ScopedComponent
  }
}

export default withProgress
